import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { isPresent, VERSION } from '@mbscan/shared'
import posthog from 'posthog-js'

import { env } from './env'

export type MonitoringUser = Readonly<{
  email: string
  id: string
  name: string
  role: string
  tenantId: string
  tenantName: string
}>

const posthogEnabled = isPresent(env.posthogApiKey) && isPresent(env.posthogApiUrl)
const fallbackConsole = globalThis.console

export function initializeMonitoring(): void {
  if (isPresent(env.bugsnagApiKey)) {
    Bugsnag.start({
      apiKey: env.bugsnagApiKey,
      appVersion: VERSION,
      plugins: [new BugsnagPluginReact()],
      releaseStage: env.mode,
    })
  }
  if (isPresent(env.posthogApiKey) && isPresent(env.posthogApiUrl)) {
    posthog.init(env.posthogApiKey, {
      api_host: env.posthogApiUrl,
      // warn/error をセッションリプレイと横断して調査できるよう、外部ログ基盤を PostHog Logs に統一する。
      logs: { serviceName: 'mbscan-client', environment: env.mode },
    })
  }
}

export function identifyMonitoringUser(user: MonitoringUser): void {
  if (isPresent(env.bugsnagApiKey)) {
    Bugsnag.setUser(user.id, user.email, user.name)
  }
  if (posthogEnabled) {
    posthog.identify(user.id, user)
  }
}

export function trackPage(pathname: string, user: MonitoringUser): void {
  // アクセス計測には集計に必要な識別子だけを送り、氏名・メールアドレス・現場名は重複送信しない。
  const properties = { pathname, role: user.role, tenantId: user.tenantId, userId: user.id }
  if (posthogEnabled && user.role !== 'admin') {
    posthog.capture('visit', properties)
  }
}

export type ReportedErrorSeverity = 'error' | 'warning' | 'info'

/**
 * info（入力ミス・権限エラー・通信断など、こちらのコードを直しても解消しない正常系）は
 * BugSnag へ送らない。サーバーの `shouldSendToBugsnag`
 * （packages/backend/integrations/src/externals/bugsnag.ts）と方針を揃える。
 * severity を info に落としても送信していたため、業務上の正常系が open のまま残り続けていた。
 *
 * **`Bugsnag.start()` の `onError` ではなく呼び出し口で止める。**
 * SDK は `start()` 側の `onError` を `notify(err, callback)` の per-call コールバックより «先に»
 * 実行するので（`@bugsnag/core` client.js）、`onError` の時点では per-call でセットする
 * `event.severity` がまだ既定値のままで、severity を見るフィルタは効かない。
 * サーバーは `event.originalError` のエラー名から severity を再計算してこれを回避しているが、
 * フロントの severity はエラーの種類ではなく errorLink が文脈から決めているため、
 * originalError からは再計算できない。
 *
 * 呼び出し口で止めるので、`window.onerror` / `unhandledrejection` のように `reportError` を
 * 通らない自動捕捉は影響を受けない（従来どおりの重さで通知される）。
 */
function shouldSendToBugsnag(severity: ReportedErrorSeverity): boolean {
  return severity !== 'info'
}

/**
 * severity は BugSnag のトリアージ用。通信断・古いバンドル・業務エラーのように、こちらのコードを
 * 直しても解消しないものまで同じ重さで通知すると、本当に見るべきエラーが埋もれる。
 * 呼び出し側で分類できないもの（画面のクラッシュ等）は既定の error のままでよい。
 */
export function reportError(cause: unknown, severity: ReportedErrorSeverity = 'error'): void {
  const error = cause instanceof Error ? cause : new Error('予期しないエラーが発生しました。')
  if (isPresent(env.bugsnagApiKey) && shouldSendToBugsnag(severity)) {
    Bugsnag.notify(error, (event) => {
      event.severity = severity
    })
  }
  if (posthogEnabled) {
    posthog.captureException(error)
    if (severity === 'error') {
      posthog.logger.error(error.message, { name: error.name })
    } else if (severity === 'warning') {
      posthog.logger.warn(error.message, { name: error.name })
    }
  } else if (severity === 'error') {
    fallbackConsole.error(error.message, { name: error.name })
  } else if (severity === 'warning') {
    fallbackConsole.warn(error.message, { name: error.name })
  }
}
